import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Search, Upload, RefreshCw, Filter, Eye, Database, TrendingUp, AlertTriangle, PieChart, Plus, Edit, Trash2, Save } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSecurityMaster = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('securities');
  const [securities, setSecurities] = useState([]);
  const [modelPortfolios, setModelPortfolios] = useState([]);
  const [restrictedAssets, setRestrictedAssets] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [riskProfiles, setRiskProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssetClass, setSelectedAssetClass] = useState('All');
  const [selectedSecurity, setSelectedSecurity] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Model Portfolio states
  const [showAddPortfolioModal, setShowAddPortfolioModal] = useState(false);
  const [showEditPortfolioModal, setShowEditPortfolioModal] = useState(false);
  const [selectedPortfolioForEdit, setSelectedPortfolioForEdit] = useState(null);
  
  // Restricted Asset states
  const [showAddRestrictedModal, setShowAddRestrictedModal] = useState(false);
  const [showEditRestrictedModal, setShowEditRestrictedModal] = useState(false);
  const [selectedRestrictedItem, setSelectedRestrictedItem] = useState(null);
  const [restrictedFormData, setRestrictedFormData] = useState({
    type: 'asset_class',
    name: '',
    reason: '',
    restriction_level: 'Complete Ban',
    effective_date: new Date().toISOString().split('T')[0],
    isin: '',
    review_date: ''
  });
  
  // Recommendations states
  const [showAddRecommendationModal, setShowAddRecommendationModal] = useState(false);
  const [showEditRecommendationModal, setShowEditRecommendationModal] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [recommendationFormData, setRecommendationFormData] = useState({
    product_name: '',
    product_type: 'Equity',
    isin: '',
    recommendation_type: 'Buy',
    target_allocation: 0,
    risk_profile_ids: [],
    rationale: '',
  });
  
  const [formData, setFormData] = useState({
    security_name: '',
    isin: '',
    symbol: '',
    asset_class: 'Equity',
    sub_asset_class: '',
    benchmark: '',
    security_type: '',
    exchange: '',
    sector: '',
    risk_rating: 'Moderate',
    status: 'Active'
  });
  
  const [portfolioFormData, setPortfolioFormData] = useState({
    name: '',
    risk_profile: 'Balanced',
    description: '',
    target_return: '',
    volatility: 'Moderate',
    rebalancing_frequency: 'Quarterly',
    minimum_investment: 5000000,
    asset_allocation: {
      Equity: 0,
      'Fixed Income': 0,
      International: 0,
      Alternatives: 0,
      Gold: 0
    }
  });

  const assetClasses = ['All', 'Equity', 'Mutual Fund', 'AIF', 'Fixed Income', 'Commodity', 'International'];

  useEffect(() => {
    if (activeTab === 'securities') {
      fetchSecurities();
    } else if (activeTab === 'models') {
      fetchModelPortfolios();
    } else if (activeTab === 'restricted') {
      fetchRestrictedAssets();
    } else if (activeTab === 'recommendations') {
      fetchRecommendations();
    }
  }, [selectedAssetClass, activeTab]);

  const fetchSecurities = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (selectedAssetClass !== 'All') {
        params.append('asset_class', selectedAssetClass);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSecurities(data.securities || []);
        setTotalCount(data.total_count || 0);
      }
    } catch (error) {
      console.error('Error fetching securities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchSecurities();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('search', searchQuery);
      
      if (selectedAssetClass !== 'All') {
        params.append('asset_class', selectedAssetClass);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSecurities(data.securities || []);
        setTotalCount(data.total_count || 0);
      }
    } catch (error) {
      console.error('Error searching securities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchModelPortfolios = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master/model-portfolios`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setModelPortfolios(data.model_portfolios || []);
      }
    } catch (error) {
      console.error('Error fetching model portfolios:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestrictedAssets = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master/restricted-assets`,
        {
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRestrictedAssets(data);
      }
    } catch (error) {
      console.error('Error fetching restricted assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const [recsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/recommendations`),
        axios.get(`${API}/risk-profiles`),
      ]);
      setRecommendations(recsRes.data);
      setRiskProfiles(profilesRes.data);
    } catch (error) {
      toast.error('Failed to fetch recommendations');
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/security-master/sync`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      
      if (response.ok) {
        toast.success('Security master synced successfully!');
        fetchSecurities();
      }
    } catch (error) {
      console.error('Error syncing security master:', error);
      toast.error('Failed to sync security master');
    } finally {
      setSyncing(false);
    }
  };

  // ============= CRUD OPERATIONS (ADMIN ONLY) =============
  
  const handleAddSecurity = () => {
    setFormData({
      security_name: '',
      isin: '',
      symbol: '',
      asset_class: 'Equity',
      sub_asset_class: '',
      benchmark: '',
      security_type: '',
      exchange: '',
      sector: '',
      risk_rating: 'Moderate',
      status: 'Active'
    });
    setEditMode(false);
    setShowAddModal(true);
  };

  const handleEditSecurity = (security) => {
    setFormData({
      security_name: security.security_name,
      isin: security.isin || '',
      symbol: security.symbol || '',
      asset_class: security.asset_class,
      sub_asset_class: security.sub_asset_class || '',
      benchmark: security.benchmark || '',
      security_type: security.security_type,
      exchange: security.exchange || '',
      sector: security.sector || '',
      risk_rating: security.risk_rating,
      status: security.status
    });
    setSelectedSecurity(security);
    setEditMode(true);
    setShowEditModal(true);
  };

  const saveSecurity = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (editMode) {
        // Update existing security
        await axios.put(`${API}/security-master/${selectedSecurity.security_id}`, formData, config);
        toast.success('Security updated successfully!');
        setShowEditModal(false);
      } else {
        // Create new security
        await axios.post(`${API}/security-master`, formData, config);
        toast.success('Security created successfully!');
        setShowAddModal(false);
      }
      
      fetchSecurities();
    } catch (error) {
      console.error('Error saving security:', error);
      toast.error(error.response?.data?.detail || 'Failed to save security');
    }
  };

  const deleteSecurity = async (securityId) => {
    if (!window.confirm('Are you sure you want to delete this security?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/security-master/${securityId}`, config);
      toast.success('Security deleted successfully!');
      fetchSecurities();
    } catch (error) {
      console.error('Error deleting security:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete security');
    }
  };

  // ============= MODEL PORTFOLIO CRUD =============
  
  const handleAddPortfolio = () => {
    setPortfolioFormData({
      name: '',
      risk_profile: 'Balanced',
      description: '',
      target_return: '',
      volatility: 'Moderate',
      rebalancing_frequency: 'Quarterly',
      minimum_investment: 5000000,
      asset_allocation: {
        Equity: 0,
        'Fixed Income': 0,
        International: 0,
        Alternatives: 0,
        Gold: 0
      }
    });
    setShowAddPortfolioModal(true);
  };

  const handleEditPortfolio = (portfolio) => {
    setPortfolioFormData({
      name: portfolio.name,
      risk_profile: portfolio.risk_profile,
      description: portfolio.description,
      target_return: portfolio.target_return,
      volatility: portfolio.volatility,
      rebalancing_frequency: portfolio.rebalancing_frequency,
      minimum_investment: portfolio.minimum_investment,
      asset_allocation: portfolio.asset_allocation
    });
    setSelectedPortfolioForEdit(portfolio);
    setShowEditPortfolioModal(true);
  };

  const savePortfolio = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const payload = {
        ...portfolioFormData,
        holdings: [] // Simplified - in real implementation, would add holdings management
      };
      
      if (selectedPortfolioForEdit) {
        await axios.put(`${API}/security-master/model-portfolios/${selectedPortfolioForEdit.id}`, payload, config);
        toast.success('Model portfolio updated successfully!');
        setShowEditPortfolioModal(false);
      } else {
        await axios.post(`${API}/security-master/model-portfolios`, payload, config);
        toast.success('Model portfolio created successfully!');
        setShowAddPortfolioModal(false);
      }
      
      fetchModelPortfolios();
    } catch (error) {
      console.error('Error saving portfolio:', error);
      toast.error(error.response?.data?.detail || 'Failed to save portfolio');
    }
  };

  const deletePortfolio = async (portfolioId) => {
    if (!window.confirm('Are you sure you want to delete this model portfolio?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/security-master/model-portfolios/${portfolioId}`, config);
      toast.success('Model portfolio deleted successfully!');
      fetchModelPortfolios();
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete portfolio');
    }
  };

  // ============= RESTRICTED ASSETS CRUD =============
  
  const handleAddRestricted = () => {
    setRestrictedFormData({
      type: 'asset_class',
      name: '',
      reason: '',
      restriction_level: 'Complete Ban',
      effective_date: new Date().toISOString().split('T')[0],
      isin: '',
      review_date: ''
    });
    setShowAddRestrictedModal(true);
  };

  const handleEditRestricted = (item, type) => {
    setRestrictedFormData({
      type: type,
      name: type === 'asset_class' ? item.asset_class : type === 'sector' ? item.sector : item.security_name,
      reason: item.reason,
      restriction_level: item.restriction_level,
      effective_date: item.effective_date,
      isin: item.isin || '',
      review_date: item.review_date || ''
    });
    setSelectedRestrictedItem({ ...item, id: item.id || `TEMP_${Date.now()}` });
    setShowEditRestrictedModal(true);
  };

  const saveRestrictedItem = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (selectedRestrictedItem && selectedRestrictedItem.id && !selectedRestrictedItem.id.startsWith('TEMP_')) {
        await axios.put(`${API}/security-master/restricted-assets/${selectedRestrictedItem.id}`, restrictedFormData, config);
        toast.success('Restricted item updated successfully!');
        setShowEditRestrictedModal(false);
      } else {
        await axios.post(`${API}/security-master/restricted-assets`, restrictedFormData, config);
        toast.success('Restricted item added successfully!');
        setShowAddRestrictedModal(false);
      }
      
      fetchRestrictedAssets();
    } catch (error) {
      console.error('Error saving restricted item:', error);
      toast.error(error.response?.data?.detail || 'Failed to save restricted item');
    }
  };

  const deleteRestrictedItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this restricted item?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/security-master/restricted-assets/${itemId}`, config);
      toast.success('Restricted item deleted successfully!');
      fetchRestrictedAssets();
    } catch (error) {
      console.error('Error deleting restricted item:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete restricted item');
    }
  };

  const deleteRecommendation = async (recId) => {
    if (!window.confirm('Are you sure you want to delete this recommendation?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.delete(`${API}/recommendations/${recId}`, config);
      toast.success('Recommendation deleted successfully!');
      fetchRecommendations();
    } catch (error) {
      console.error('Error deleting recommendation:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete recommendation');
    }
  };

  const handleRecommendationSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (selectedRecommendation) {
        // Update existing
        await axios.put(`${API}/recommendations/${selectedRecommendation.id}`, recommendationFormData, config);
        toast.success('Recommendation updated successfully!');
      } else {
        // Create new
        await axios.post(`${API}/recommendations`, recommendationFormData, config);
        toast.success('Recommendation created successfully!');
      }
      
      setShowAddRecommendationModal(false);
      setShowEditRecommendationModal(false);
      setSelectedRecommendation(null);
      fetchRecommendations();
    } catch (error) {
      console.error('Error saving recommendation:', error);
      toast.error(error.response?.data?.detail || 'Failed to save recommendation');
    }
  };

  const viewSecurityDetail = (security) => {
    setSelectedSecurity(security);
    setShowDetailModal(true);
  };

  const getRiskBadgeColor = (rating) => {
    const colors = {
      'Very Low': 'bg-green-100 text-green-800',
      'Low': 'bg-green-200 text-green-900',
      'Moderate': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-[#fffdf0] text-ugmonk-darker',
      'Very High': 'bg-red-100 text-red-800'
    };
    return colors[rating] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Security Master
            </h1>
            <p className="text-gray-600">
              Investment products, model portfolios & restrictions
            </p>
          </div>
          
          {/* Sync Action */}
          {activeTab === 'securities' && (
            <Button 
              onClick={handleSync}
              disabled={syncing}
              className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Sync Data
                </>
              )}
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('securities')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'securities'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              All Securities
            </button>
            <button
              onClick={() => setActiveTab('models')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'models'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <PieChart className="w-4 h-4 inline mr-2" />
              Model Portfolios
            </button>
            <button
              onClick={() => setActiveTab('restricted')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'restricted'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Restricted Assets
            </button>
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'recommendations'
                  ? 'border-[#ffd87a] text-ugmonk-dark'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline mr-2" />
              Recommendations
            </button>
          </div>
        </div>

        {/* Securities Tab Content */}
        {activeTab === 'securities' && (
          <>
            {/* Search and Filter Controls */}
        <Card className="border-gray-200 bg-white">
          <CardContent className="pt-6 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search */}
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search by name, ISIN, or symbol..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ffd87a] focus:border-transparent"
                  />
                  <button
                    onClick={handleSearch}
                    className="absolute right-2 top-2 px-4 py-1.5 bg-[#ffd87a] text-white rounded-md hover:bg-[#f5cc6a] transition-colors text-sm font-medium"
                  >
                    Search
                  </button>
                </div>
              </div>

              {/* Results Count */}
              <div className="flex items-center justify-center md:justify-end px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                <Database className="w-4 h-4 text-gray-600 mr-2" />
                <span className="text-sm text-gray-700">
                  <span className="font-semibold">{securities.length}</span> of{' '}
                  <span className="font-semibold">{totalCount}</span> securities
                </span>
              </div>
            </div>

            {/* Asset Class Filter */}
            <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-200">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter size={18} className="text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Asset Class:</span>
                {assetClasses.map((assetClass) => (
                  <button
                    key={assetClass}
                    onClick={() => setSelectedAssetClass(assetClass)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedAssetClass === assetClass
                        ? 'bg-[#ffd87a] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {assetClass}
                  </button>
                ))}
              </div>
              
              {/* Add Security Button (Admin Only) */}
              <Button 
                onClick={handleAddSecurity}
                className="bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white flex items-center gap-2"
              >
                <Plus size={16} />
                Add Security
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Securities Table */}
        <Card className="border-gray-200 bg-white">
          <CardHeader className="pb-3 bg-white">
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-ugmonk-dark" />
              Investment Securities
            </CardTitle>
          </CardHeader>
          <CardContent className="bg-white">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="animate-spin text-ugmonk-dark" size={40} />
              </div>
            ) : securities.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <Database className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg mb-2">No securities found</p>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Security Name
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Symbol / ISIN
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Asset Class
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Sub-Asset Class
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Benchmark
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Risk Rating
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {securities.map((security, index) => (
                      <tr
                        key={security.security_id || index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{security.security_name}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{security.symbol}</div>
                          <div className="text-xs text-gray-500">{security.isin}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-[#fffdf0] text-ugmonk-dark">
                            {security.asset_class}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {security.sub_asset_class}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {security.benchmark}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(security.risk_rating)}`}>
                            {security.risk_rating}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => viewSecurityDetail(security)}
                              className="text-ugmonk-dark hover:text-[#f5cc6a] flex items-center gap-1 text-sm font-medium"
                            >
                              <Eye size={16} />
                              View
                            </button>
                            <button
                              onClick={() => handleEditSecurity(security)}
                              className="text-ugmonk-dark hover:text-[#1a1a1a] flex items-center gap-1 text-sm font-medium"
                            >
                              <Edit size={16} />
                              Edit
                            </button>
                            <button
                              onClick={() => deleteSecurity(security.security_id)}
                              className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm font-medium"
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}

        {/* Model Portfolios Tab Content */}
        {activeTab === 'models' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Model Portfolios</h2>
              <Button 
                onClick={handleAddPortfolio}
                className="bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white flex items-center gap-2"
              >
                <Plus size={16} />
                Add Model Portfolio
              </Button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-3 flex items-center justify-center py-20">
                <RefreshCw className="animate-spin text-ugmonk-dark" size={40} />
              </div>
            ) : (
              modelPortfolios.map((portfolio) => (
                <Card key={portfolio.id} className="border-gray-200 hover:shadow-lg transition-shadow bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg text-gray-900 mb-2">
                          {portfolio.name}
                        </CardTitle>
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                          portfolio.risk_profile === 'Conservative' ? 'bg-green-100 text-green-800' :
                          portfolio.risk_profile === 'Balanced' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-[#fffdf0] text-ugmonk-darker'
                        }`}>
                          {portfolio.risk_profile}
                        </span>
                      </div>
                      <TrendingUp className="w-5 h-5 text-ugmonk-dark" />
                    </div>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <p className="text-sm text-gray-600 mb-4">{portfolio.description}</p>
                    
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Target Return:</span>
                        <span className="font-semibold text-gray-900">{portfolio.target_return}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Volatility:</span>
                        <span className="font-semibold text-gray-900">{portfolio.volatility}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Min Investment:</span>
                        <span className="font-semibold text-gray-900">
                          ₹{(portfolio.minimum_investment / 10000000).toFixed(1)}Cr
                        </span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Asset Allocation:</p>
                      <div className="space-y-1">
                        {Object.entries(portfolio.asset_allocation).map(([asset, percentage]) => (
                          percentage > 0 && (
                            <div key={asset} className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-600">{asset}</span>
                                  <span className="font-medium">{percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className="bg-[#1a1a1a] h-1.5 rounded-full" 
                                    style={{width: `${percentage}%`}}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setSelectedPortfolio(portfolio);
                          setShowPortfolioModal(true);
                        }}
                        variant="outline"
                        className="flex-1 border-[#ffd87a] text-ugmonk-dark hover:bg-[#fffdf0]"
                      >
                        View Holdings
                      </Button>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button
                        onClick={() => handleEditPortfolio(portfolio)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-[#1a1a1a] text-ugmonk-dark hover:bg-[#fffdf0] flex items-center justify-center gap-1"
                      >
                        <Edit size={14} />
                        Edit
                      </Button>
                      <Button
                        onClick={() => deletePortfolio(portfolio.id)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-red-600 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1"
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          </>
        )}

        {/* Restricted Assets Tab Content */}
        {activeTab === 'restricted' && restrictedAssets && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Restricted Assets</h2>
              <Button 
                onClick={handleAddRestricted}
                className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
              >
                <Plus size={16} />
                Add Restricted Item
              </Button>
            </div>
            <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="animate-spin text-ugmonk-dark" size={40} />
              </div>
            ) : (
              <>
                {/* Restricted Asset Classes */}
                <Card className="border-gray-200 bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Restricted Asset Classes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <div className="overflow-x-auto">
                      <table className="w-full bg-white">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Asset Class</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Reason</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Restriction Level</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Effective Date</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {restrictedAssets.restricted_asset_classes.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">{item.asset_class}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.reason}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                  item.restriction_level.includes('Complete') 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {item.restriction_level}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.effective_date}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleEditRestricted(item, 'asset_class')}
                                    className="text-ugmonk-dark hover:text-[#1a1a1a] flex items-center gap-1 text-sm"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    onClick={() => deleteRestrictedItem(item.id || `TEMP_AC_${idx}`)}
                                    className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Restricted Sectors */}
                <Card className="border-gray-200 bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-ugmonk-dark" />
                      Restricted Sectors (ESG)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {restrictedAssets.restricted_sectors.map((item, idx) => (
                        <div key={idx} className="p-4 border border-red-200 rounded-lg bg-red-50 relative">
                          <div className="absolute top-2 right-2 flex gap-1">
                            <button
                              onClick={() => handleEditRestricted(item, 'sector')}
                              className="p-1 bg-white rounded text-ugmonk-dark hover:bg-gray-100"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => deleteRestrictedItem(item.id || `TEMP_SEC_${idx}`)}
                              className="p-1 bg-white rounded text-red-600 hover:bg-gray-100"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2 pr-16">{item.sector}</h4>
                          <p className="text-sm text-gray-600 mb-2">{item.reason}</p>
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {item.restriction_level}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Restricted Securities */}
                <Card className="border-gray-200 bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Restricted Securities
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Security</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">ISIN</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Reason</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {restrictedAssets.restricted_securities.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">{item.security_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.isin}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.reason}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                  item.restriction_level === 'Complete Ban' 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {item.restriction_level}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleEditRestricted(item, 'security')}
                                    className="text-ugmonk-dark hover:text-[#1a1a1a] flex items-center gap-1 text-sm"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    onClick={() => deleteRestrictedItem(item.id || `TEMP_SEC_${idx}`)}
                                    className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Investment Guidelines */}
                <Card className="border-gray-200 bg-white">
                  <CardHeader className="pb-3 bg-white">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <Database className="w-5 h-5 text-ugmonk-dark" />
                      Investment Guidelines
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {restrictedAssets.investment_guidelines.map((item, idx) => (
                        <div key={idx} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-[#ffd87a] rounded-full mt-2"></div>
                            <div>
                              <p className="font-medium text-gray-900 mb-1">{item.guideline}</p>
                              <p className="text-xs text-gray-600">
                                <span className="font-semibold">{item.category}</span> · {item.applicable_to}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
          </>
        )}

        {/* Recommendations Tab Content */}
        {activeTab === 'recommendations' && (
          <div className="mt-6">
            <Card className="border-gray-200 bg-white">
              <CardHeader className="pb-3 bg-white">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-ugmonk-dark" />
                    Product Recommendations
                  </CardTitle>
                  {user?.role === 'admin' && (
                    <Button
                      onClick={() => {
                        setRecommendationFormData({
                          product_name: '',
                          product_type: 'Equity',
                          isin: '',
                          recommendation_type: 'Buy',
                          target_allocation: 0,
                          risk_profile_ids: [],
                          rationale: '',
                        });
                        setShowAddRecommendationModal(true);
                      }}
                      className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Recommendation
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="bg-white">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a1a]"></div>
                  </div>
                ) : recommendations.length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-semibold text-gray-900 mb-2">No Recommendations Available</p>
                    <p className="text-gray-600">
                      {user?.role === 'admin' 
                        ? 'Click "Add Recommendation" to create the first recommendation' 
                        : 'No recommendations have been added yet'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b-2 border-gray-200 bg-gray-50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">ISIN</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Recommendation</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Target %</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Risk Profiles</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Rationale</th>
                          {user?.role === 'admin' && (
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {recommendations.map((rec, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{rec.product_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{rec.product_type}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 font-mono">{rec.isin || '-'}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                                rec.recommendation_type === 'Buy' 
                                  ? 'bg-green-100 text-green-800' 
                                  : rec.recommendation_type === 'Sell'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {rec.recommendation_type}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-semibold">{rec.target_allocation}%</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div className="flex flex-wrap gap-1">
                                {rec.risk_profile_names?.map((profile, i) => (
                                  <span key={i} className="inline-flex px-2 py-0.5 bg-[#fffdf0] text-ugmonk-dark rounded text-xs">
                                    {profile}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={rec.rationale}>
                              {rec.rationale}
                            </td>
                            {user?.role === 'admin' && (
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      setSelectedRecommendation(rec);
                                      setRecommendationFormData({
                                        product_name: rec.product_name,
                                        product_type: rec.product_type,
                                        isin: rec.isin || '',
                                        recommendation_type: rec.recommendation_type,
                                        target_allocation: rec.target_allocation,
                                        risk_profile_ids: rec.risk_profile_ids || [],
                                        rationale: rec.rationale,
                                      });
                                      setShowEditRecommendationModal(true);
                                    }}
                                    className="text-ugmonk-dark hover:text-[#1a1a1a] flex items-center gap-1 text-sm"
                                  >
                                    <Edit size={14} />
                                  </button>
                                  <button
                                    onClick={() => deleteRecommendation(rec.id)}
                                    className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Security Detail Modal */}
      {showDetailModal && selectedSecurity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Security Details</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Security Name</label>
                  <p className="text-lg text-gray-900">{selectedSecurity.security_name}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Symbol</label>
                    <p className="text-gray-900">{selectedSecurity.symbol}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">ISIN</label>
                    <p className="text-gray-900">{selectedSecurity.isin}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Asset Class</label>
                    <p className="text-gray-900">{selectedSecurity.asset_class}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Sub-Asset Class</label>
                    <p className="text-gray-900">{selectedSecurity.sub_asset_class}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Benchmark</label>
                  <p className="text-gray-900">{selectedSecurity.benchmark}</p>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Security Type</label>
                  <p className="text-gray-900">{selectedSecurity.security_type}</p>
                </div>

                {selectedSecurity.sector && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Sector</label>
                    <p className="text-gray-900">{selectedSecurity.sector}</p>
                  </div>
                )}

                {selectedSecurity.exchange && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Exchange</label>
                    <p className="text-gray-900">{selectedSecurity.exchange}</p>
                  </div>
                )}

                {selectedSecurity.amc_name && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">AMC Name</label>
                    <p className="text-gray-900">{selectedSecurity.amc_name}</p>
                  </div>
                )}

                {selectedSecurity.expense_ratio && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Expense Ratio</label>
                    <p className="text-gray-900">{selectedSecurity.expense_ratio}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-semibold text-gray-600">Risk Rating</label>
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(selectedSecurity.risk_rating)}`}>
                    {selectedSecurity.risk_rating}
                  </span>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Status</label>
                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {selectedSecurity.status}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Detail Modal */}
      {showPortfolioModal && selectedPortfolio && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedPortfolio.name}</h2>
                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                    selectedPortfolio.risk_profile === 'Conservative' ? 'bg-green-100 text-green-800' :
                    selectedPortfolio.risk_profile === 'Balanced' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-[#fffdf0] text-ugmonk-darker'
                  }`}>
                    {selectedPortfolio.risk_profile}
                  </span>
                </div>
                <button
                  onClick={() => setShowPortfolioModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Target Return</label>
                    <p className="text-lg font-bold text-ugmonk-dark">{selectedPortfolio.target_return}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Volatility</label>
                    <p className="text-lg font-bold text-gray-900">{selectedPortfolio.volatility}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Rebalancing</label>
                    <p className="text-lg font-bold text-gray-900">{selectedPortfolio.rebalancing_frequency}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Minimum Investment</label>
                    <p className="text-lg font-bold text-gray-900">
                      ₹{(selectedPortfolio.minimum_investment / 10000000).toFixed(1)}Cr
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Portfolio Holdings</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Security</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Asset Class</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Allocation %</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Rationale</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedPortfolio.holdings.map((holding, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{holding.security_name}</div>
                              <div className="text-xs text-gray-500">{holding.security_id}</div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-[#fffdf0] text-ugmonk-dark">
                                {holding.asset_class}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold text-ugmonk-dark">{holding.allocation_percentage}%</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{holding.rationale}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPortfolioModal(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Security Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-2xl font-bold flex items-center gap-2">
              <Plus className="w-6 h-6 text-ugmonk-dark" />
              Add New Security
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="security_name" className="text-gray-700">Security Name *</Label>
                <Input
                  id="security_name"
                  value={formData.security_name}
                  onChange={(e) => setFormData({...formData, security_name: e.target.value})}
                  required
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="isin" className="text-gray-700">ISIN</Label>
                <Input
                  id="isin"
                  value={formData.isin}
                  onChange={(e) => setFormData({...formData, isin: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="symbol" className="text-gray-700">Symbol</Label>
                <Input
                  id="symbol"
                  value={formData.symbol}
                  onChange={(e) => setFormData({...formData, symbol: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="asset_class" className="text-gray-700">Asset Class *</Label>
                <select
                  id="asset_class"
                  value={formData.asset_class}
                  onChange={(e) => setFormData({...formData, asset_class: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1a1a1a]"
                >
                  <option value="Equity">Equity</option>
                  <option value="Mutual Fund">Mutual Fund</option>
                  <option value="AIF">AIF</option>
                  <option value="Fixed Income">Fixed Income</option>
                  <option value="Commodity">Commodity</option>
                  <option value="International">International</option>
                </select>
              </div>
              <div>
                <Label htmlFor="sub_asset_class" className="text-gray-700">Sub Asset Class</Label>
                <Input
                  id="sub_asset_class"
                  value={formData.sub_asset_class}
                  onChange={(e) => setFormData({...formData, sub_asset_class: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="security_type" className="text-gray-700">Security Type *</Label>
                <Input
                  id="security_type"
                  value={formData.security_type}
                  onChange={(e) => setFormData({...formData, security_type: e.target.value})}
                  placeholder="e.g., Stock, Bond, Mutual Fund"
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="benchmark" className="text-gray-700">Benchmark</Label>
                <Input
                  id="benchmark"
                  value={formData.benchmark}
                  onChange={(e) => setFormData({...formData, benchmark: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="exchange" className="text-gray-700">Exchange</Label>
                <Input
                  id="exchange"
                  value={formData.exchange}
                  onChange={(e) => setFormData({...formData, exchange: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="sector" className="text-gray-700">Sector</Label>
                <Input
                  id="sector"
                  value={formData.sector}
                  onChange={(e) => setFormData({...formData, sector: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="risk_rating" className="text-gray-700">Risk Rating *</Label>
                <select
                  id="risk_rating"
                  value={formData.risk_rating}
                  onChange={(e) => setFormData({...formData, risk_rating: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1a1a1a]"
                >
                  <option value="Very Low">Very Low</option>
                  <option value="Low">Low</option>
                  <option value="Moderate">Moderate</option>
                  <option value="High">High</option>
                  <option value="Very High">Very High</option>
                </select>
              </div>
              <div>
                <Label htmlFor="status" className="text-gray-700">Status *</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1a1a1a]"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={saveSecurity}
                className="flex-1 bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white flex items-center justify-center gap-2"
              >
                <Save size={16} />
                Save Security
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Security Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-2xl font-bold flex items-center gap-2">
              <Edit className="w-6 h-6 text-ugmonk-dark" />
              Edit Security
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_security_name" className="text-gray-700">Security Name *</Label>
                <Input
                  id="edit_security_name"
                  value={formData.security_name}
                  onChange={(e) => setFormData({...formData, security_name: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="edit_isin" className="text-gray-700">ISIN</Label>
                <Input
                  id="edit_isin"
                  value={formData.isin}
                  onChange={(e) => setFormData({...formData, isin: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="edit_symbol" className="text-gray-700">Symbol</Label>
                <Input
                  id="edit_symbol"
                  value={formData.symbol}
                  onChange={(e) => setFormData({...formData, symbol: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="edit_asset_class" className="text-gray-700">Asset Class *</Label>
                <select
                  id="edit_asset_class"
                  value={formData.asset_class}
                  onChange={(e) => setFormData({...formData, asset_class: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1a1a1a]"
                >
                  <option value="Equity">Equity</option>
                  <option value="Mutual Fund">Mutual Fund</option>
                  <option value="AIF">AIF</option>
                  <option value="Fixed Income">Fixed Income</option>
                  <option value="Commodity">Commodity</option>
                  <option value="International">International</option>
                </select>
              </div>
              <div>
                <Label htmlFor="edit_sub_asset_class" className="text-gray-700">Sub Asset Class</Label>
                <Input
                  id="edit_sub_asset_class"
                  value={formData.sub_asset_class}
                  onChange={(e) => setFormData({...formData, sub_asset_class: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="edit_security_type" className="text-gray-700">Security Type *</Label>
                <Input
                  id="edit_security_type"
                  value={formData.security_type}
                  onChange={(e) => setFormData({...formData, security_type: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="edit_benchmark" className="text-gray-700">Benchmark</Label>
                <Input
                  id="edit_benchmark"
                  value={formData.benchmark}
                  onChange={(e) => setFormData({...formData, benchmark: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="edit_exchange" className="text-gray-700">Exchange</Label>
                <Input
                  id="edit_exchange"
                  value={formData.exchange}
                  onChange={(e) => setFormData({...formData, exchange: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="edit_sector" className="text-gray-700">Sector</Label>
                <Input
                  id="edit_sector"
                  value={formData.sector}
                  onChange={(e) => setFormData({...formData, sector: e.target.value})}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label htmlFor="edit_risk_rating" className="text-gray-700">Risk Rating *</Label>
                <select
                  id="edit_risk_rating"
                  value={formData.risk_rating}
                  onChange={(e) => setFormData({...formData, risk_rating: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1a1a1a]"
                >
                  <option value="Very Low">Very Low</option>
                  <option value="Low">Low</option>
                  <option value="Moderate">Moderate</option>
                  <option value="High">High</option>
                  <option value="Very High">Very High</option>
                </select>
              </div>
              <div>
                <Label htmlFor="edit_status" className="text-gray-700">Status *</Label>
                <select
                  id="edit_status"
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1a1a1a]"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                onClick={saveSecurity}
                className="flex-1 bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white flex items-center justify-center gap-2"
              >
                <Save size={16} />
                Update Security
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Model Portfolio Dialogs */}
      <Dialog open={showAddPortfolioModal || showEditPortfolioModal} onOpenChange={(open) => {
        setShowAddPortfolioModal(false);
        setShowEditPortfolioModal(false);
      }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-2xl font-bold">
              {showEditPortfolioModal ? 'Edit Model Portfolio' : 'Add Model Portfolio'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Portfolio Name *</Label>
                <Input
                  value={portfolioFormData.name}
                  onChange={(e) => setPortfolioFormData({...portfolioFormData, name: e.target.value})}
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Risk Profile *</Label>
                <select
                  value={portfolioFormData.risk_profile}
                  onChange={(e) => setPortfolioFormData({...portfolioFormData, risk_profile: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="Conservative">Conservative</option>
                  <option value="Balanced">Balanced</option>
                  <option value="Aggressive">Aggressive</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Input
                  value={portfolioFormData.description}
                  onChange={(e) => setPortfolioFormData({...portfolioFormData, description: e.target.value})}
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Target Return</Label>
                <Input
                  value={portfolioFormData.target_return}
                  onChange={(e) => setPortfolioFormData({...portfolioFormData, target_return: e.target.value})}
                  placeholder="e.g., 10-12%"
                  className="bg-white"
                />
              </div>
              <div>
                <Label>Volatility</Label>
                <select
                  value={portfolioFormData.volatility}
                  onChange={(e) => setPortfolioFormData({...portfolioFormData, volatility: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="Low">Low</option>
                  <option value="Moderate">Moderate</option>
                  <option value="High">High</option>
                </select>
              </div>
              <div>
                <Label>Rebalancing Frequency</Label>
                <select
                  value={portfolioFormData.rebalancing_frequency}
                  onChange={(e) => setPortfolioFormData({...portfolioFormData, rebalancing_frequency: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Semi-Annual">Semi-Annual</option>
                  <option value="Annual">Annual</option>
                </select>
              </div>
              <div>
                <Label>Minimum Investment (₹)</Label>
                <Input
                  type="number"
                  value={portfolioFormData.minimum_investment}
                  onChange={(e) => setPortfolioFormData({...portfolioFormData, minimum_investment: parseFloat(e.target.value)})}
                  className="bg-white"
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-lg font-semibold mb-3 block">Asset Allocation (%)</Label>
              <div className="grid grid-cols-5 gap-3">
                {Object.keys(portfolioFormData.asset_allocation).map((asset) => (
                  <div key={asset}>
                    <Label className="text-sm">{asset}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={portfolioFormData.asset_allocation[asset]}
                      onChange={(e) => setPortfolioFormData({
                        ...portfolioFormData,
                        asset_allocation: {
                          ...portfolioFormData.asset_allocation,
                          [asset]: parseFloat(e.target.value) || 0
                        }
                      })}
                      className="bg-white"
                    />
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Total: {Object.values(portfolioFormData.asset_allocation).reduce((a, b) => a + b, 0)}%
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={savePortfolio}
                className="flex-1 bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white"
              >
                <Save size={16} className="mr-2" />
                {showEditPortfolioModal ? 'Update' : 'Save'} Portfolio
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddPortfolioModal(false);
                  setShowEditPortfolioModal(false);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Restricted Item Dialogs */}
      <Dialog open={showAddRestrictedModal || showEditRestrictedModal} onOpenChange={(open) => {
        setShowAddRestrictedModal(false);
        setShowEditRestrictedModal(false);
      }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900 text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              {showEditRestrictedModal ? 'Edit Restricted Item' : 'Add Restricted Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Restriction Type *</Label>
              <select
                value={restrictedFormData.type}
                onChange={(e) => setRestrictedFormData({...restrictedFormData, type: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
                disabled={showEditRestrictedModal}
              >
                <option value="asset_class">Asset Class</option>
                <option value="sector">Sector</option>
                <option value="security">Security</option>
              </select>
            </div>
            
            <div>
              <Label>{restrictedFormData.type === 'security' ? 'Security Name' : restrictedFormData.type === 'sector' ? 'Sector Name' : 'Asset Class'} *</Label>
              <Input
                value={restrictedFormData.name}
                onChange={(e) => setRestrictedFormData({...restrictedFormData, name: e.target.value})}
                className="bg-white"
              />
            </div>

            {restrictedFormData.type === 'security' && (
              <div>
                <Label>ISIN</Label>
                <Input
                  value={restrictedFormData.isin}
                  onChange={(e) => setRestrictedFormData({...restrictedFormData, isin: e.target.value})}
                  className="bg-white"
                />
              </div>
            )}

            <div>
              <Label>Reason *</Label>
              <Input
                value={restrictedFormData.reason}
                onChange={(e) => setRestrictedFormData({...restrictedFormData, reason: e.target.value})}
                placeholder="e.g., High volatility, Regulatory concerns"
                className="bg-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Restriction Level *</Label>
                <select
                  value={restrictedFormData.restriction_level}
                  onChange={(e) => setRestrictedFormData({...restrictedFormData, restriction_level: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="Complete Ban">Complete Ban</option>
                  <option value="Limited Exposure">Limited Exposure</option>
                  <option value="Requires Approval">Requires Approval</option>
                </select>
              </div>

              <div>
                <Label>Effective Date *</Label>
                <Input
                  type="date"
                  value={restrictedFormData.effective_date}
                  onChange={(e) => setRestrictedFormData({...restrictedFormData, effective_date: e.target.value})}
                  className="bg-white"
                />
              </div>
            </div>

            <div>
              <Label>Review Date (Optional)</Label>
              <Input
                type="date"
                value={restrictedFormData.review_date}
                onChange={(e) => setRestrictedFormData({...restrictedFormData, review_date: e.target.value})}
                className="bg-white"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={saveRestrictedItem}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                <Save size={16} className="mr-2" />
                {showEditRestrictedModal ? 'Update' : 'Add'} Restricted Item
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddRestrictedModal(false);
                  setShowEditRestrictedModal(false);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Recommendation Modal */}
      <Dialog 
        open={showAddRecommendationModal || showEditRecommendationModal} 
        onOpenChange={(open) => {
          if (!open) {
            setShowAddRecommendationModal(false);
            setShowEditRecommendationModal(false);
            setSelectedRecommendation(null);
          }
        }}
      >
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900">
              {selectedRecommendation ? 'Edit Recommendation' : 'Add Product Recommendation'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecommendationSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rec_product_name" className="text-gray-700">Product Name *</Label>
                <Input
                  id="rec_product_name"
                  value={recommendationFormData.product_name}
                  onChange={(e) => setRecommendationFormData({ ...recommendationFormData, product_name: e.target.value })}
                  required
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
              <div>
                <Label htmlFor="rec_product_type" className="text-gray-700">Product Type *</Label>
                <select
                  id="rec_product_type"
                  value={recommendationFormData.product_type}
                  onChange={(e) => setRecommendationFormData({ ...recommendationFormData, product_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                >
                  <option value="Equity">Equity</option>
                  <option value="Debt">Debt</option>
                  <option value="Mutual Fund">Mutual Fund</option>
                  <option value="ETF">ETF</option>
                  <option value="Alternative">Alternative</option>
                </select>
              </div>
              <div>
                <Label htmlFor="rec_isin" className="text-gray-700">ISIN</Label>
                <Input
                  id="rec_isin"
                  value={recommendationFormData.isin}
                  onChange={(e) => setRecommendationFormData({ ...recommendationFormData, isin: e.target.value })}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
              <div>
                <Label htmlFor="rec_recommendation_type" className="text-gray-700">Recommendation *</Label>
                <select
                  id="rec_recommendation_type"
                  value={recommendationFormData.recommendation_type}
                  onChange={(e) => setRecommendationFormData({ ...recommendationFormData, recommendation_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                >
                  <option value="Buy">Buy</option>
                  <option value="Hold">Hold</option>
                  <option value="Sell">Sell</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label htmlFor="rec_target_allocation" className="text-gray-700">Target Allocation (%) *</Label>
                <Input
                  id="rec_target_allocation"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={recommendationFormData.target_allocation}
                  onChange={(e) => setRecommendationFormData({ ...recommendationFormData, target_allocation: parseFloat(e.target.value) })}
                  required
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-700">Applicable Risk Profiles *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {riskProfiles.map((profile) => (
                    <label key={profile.id} className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={recommendationFormData.risk_profile_ids.includes(profile.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setRecommendationFormData({ 
                              ...recommendationFormData, 
                              risk_profile_ids: [...recommendationFormData.risk_profile_ids, profile.id] 
                            });
                          } else {
                            setRecommendationFormData({ 
                              ...recommendationFormData, 
                              risk_profile_ids: recommendationFormData.risk_profile_ids.filter((id) => id !== profile.id) 
                            });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-900">{profile.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <Label htmlFor="rec_rationale" className="text-gray-700">Rationale *</Label>
                <textarea
                  id="rec_rationale"
                  value={recommendationFormData.rationale}
                  onChange={(e) => setRecommendationFormData({ ...recommendationFormData, rationale: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded-md bg-white border border-gray-300 text-gray-900"
                  rows={4}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button 
                type="submit" 
                className="flex-1 bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
              >
                {selectedRecommendation ? 'Update Recommendation' : 'Add Recommendation'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddRecommendationModal(false);
                  setShowEditRecommendationModal(false);
                  setSelectedRecommendation(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminSecurityMaster;
